/**
 * Diagnóstico del flujo de dimensiones (morphs / escala / catálogo / zócalo).
 *
 * INACTIVO por defecto. Activar: window.__DIM_FLOW_ON__ = true
 * (o VITE_DIM_FLOW_DIAGNOSTIC=true). Filtrar consola por: DimFlow
 *
 * No altera comportamiento del configurador: solo observa y registra.
 */

declare global {
  interface Window {
    __DIM_FLOW_OFF__?: boolean
    __DIM_FLOW_ON__?: boolean
  }
}

export type DimFlowPhase =
  | 'catalog.load'
  | 'unit.create'
  | 'morphs.parse'
  | 'morphs.apply'
  | 'scale.compute'
  | 'obb.update'
  | 'sidebar.read'
  | 'sidebar.persist'
  | 'morph.edit'
  | 'render.morphs'
  | 'render.scale'
  | 'dimension.change'

export type DimFlowSnapshot = {
  id?: string | number
  referencia?: string
  catalogID?: string | number
  tipoNormalizado?: string
  description?: string
  /** Dimensiones cabecera catálogo (API ancho/alto/fondo) */
  catalogHeaderMm?: { ancho?: number; alto?: number; fondo?: number }
  /** width / height / thickness en store */
  storeMm?: { width?: number; height?: number; thickness?: number }
  /** Bbox GLB en mm */
  glbBaseMm?: { x?: number; y?: number; z?: number }
  freeDimensionCatalogMm?: { x?: number; y?: number; z?: number }
  freeDimensionScaleBaseMm?: { x?: number; y?: number; z?: number }
  freeDimensionFlags?: {
    anchoViaGlbMorph?: boolean
    altoViaGlbMorph?: boolean
    fondoViaGlbMorph?: boolean
  }
  morphsAvailable?: Array<{
    name: string
    min?: number
    max?: number
    default?: number
    _default?: number
    modifyable?: boolean
  }>
  morphsActive?: Array<{ name: string; default?: number; _default?: number }>
  scaleApplied?: { x?: number; y?: number; z?: number }
  usesMorph?: boolean
  usesScale?: boolean
  usesFreeDimension?: boolean
  hasMorphs?: boolean
  /** Estimación tamaño visual mm ≈ glbBase * scale (o morph) */
  visualEstMm?: { x?: number; y?: number; z?: number }
  sidebarMm?: { ancho?: number; alto?: number; fondo?: number }
  boundingBoxHalfSize?: { x?: number; y?: number; z?: number }
  matrixElements?: number[]
  willNeedIndependentZocalo?: boolean
  zocaloTypeHint?: string | null
  extra?: Record<string, unknown>
}

type DimFlowLogPayload = {
  file: string
  fn: string
  phase: DimFlowPhase
  reason: string
  snapshot: DimFlowSnapshot
}

let seq = 0
let bannerShown = false

/**
 * Logs solo si se activa explícitamente:
 * - window.__DIM_FLOW_ON__ = true
 * - VITE_DIM_FLOW_DIAGNOSTIC=true
 */
function isDimFlowDiagnosticEnabled(): boolean {
  if (typeof window !== 'undefined' && window.__DIM_FLOW_OFF__ === true) return false
  if (typeof window !== 'undefined' && window.__DIM_FLOW_ON__ === true) return true
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_DIM_FLOW_DIAGNOSTIC === 'true') {
    return true
  }
  return false
}

function showBannerOnce(): void {
  if (bannerShown || typeof console === 'undefined') return
  if (!isDimFlowDiagnosticEnabled()) return
  bannerShown = true
  console.log(
    '%c[DimFlow] Diagnóstico de dimensiones ACTIVO',
    'background:#0b5;color:#fff;padding:4px 8px;font-weight:bold;border-radius:4px',
    '\nFiltra la consola por "DimFlow". Desactivar: window.__DIM_FLOW_OFF__ = true',
  )
}

/** Heurística: MBJ / COL no-DECO llevan zócalo; COL DECO no. */
export function inferWillNeedIndependentZocalo(item?: {
  tipoNormalizado?: string
  description?: string
  catalogID?: string | number
  referencia?: string
} | null): { willNeed: boolean; zocaloTypeHint: string | null } {
  if (!item) return { willNeed: false, zocaloTypeHint: null }
  const tipo = String(item.tipoNormalizado ?? '').toUpperCase()
  const desc = String(item.description ?? '').toLowerCase()
  const code = String(item.catalogID ?? '').toUpperCase()
  const ref = String(item.referencia ?? '').toUpperCase()
  if (desc.includes('zocalo') || code.includes('ZOCALO')) {
    return { willNeed: false, zocaloTypeHint: 'IS_ZOCALO' }
  }
  const isDeco =
    code.includes('DECO') || ref.includes('DECO') || desc.includes('deco')
  // Columnas DECO: sin zócalo independiente.
  if (tipo === 'COL' && isDeco) {
    return { willNeed: false, zocaloTypeHint: 'COL_DECO_NO_ZOCALO' }
  }
  if (tipo === 'MBJ' || tipo === 'COL') {
    return { willNeed: true, zocaloTypeHint: 'ZOCALO 4000 (catálogo existente)' }
  }
  return { willNeed: false, zocaloTypeHint: null }
}

function compactMorphs(
  morphs?: Array<{
    name?: string
    min?: number
    max?: number
    default?: number
    _default?: number
    modifyable?: boolean
  }> | null,
) {
  if (!morphs?.length) return undefined
  return morphs.map((m) => ({
    name: m.name ?? '?',
    min: m.min,
    max: m.max,
    default: m.default,
    _default: m._default,
    modifyable: m.modifyable,
  }))
}

export function buildDimFlowSnapshot(item: {
  id?: string | number
  referencia?: string
  catalogID?: string | number
  tipoNormalizado?: string
  description?: string
  width?: number
  height?: number
  thickness?: number
  hasMorphs?: boolean
  morphs?: Array<{
    name?: string
    min?: number
    max?: number
    default?: number
    _default?: number
    modifyable?: boolean
  }>
  freeDimensionGlbBaseMm?: { x?: number; y?: number; z?: number }
  freeDimensionCatalogMm?: { x?: number; y?: number; z?: number }
  freeDimensionScaleBaseMm?: { x?: number; y?: number; z?: number }
  freeDimensionAnchoViaGlbMorph?: boolean
  freeDimensionAltoViaGlbMorph?: boolean
  freeDimensionFondoViaGlbMorph?: boolean
  matrix?: { elements?: ArrayLike<number> }
  boundingBox?: { halfSize?: { x?: number; y?: number; z?: number } }
} | null | undefined, extra?: Record<string, unknown>): DimFlowSnapshot {
  if (!item) return { extra }
  const zocalo = inferWillNeedIndependentZocalo(item)
  const glb = item.freeDimensionGlbBaseMm
  return {
    id: item.id,
    referencia: item.referencia,
    catalogID: item.catalogID,
    tipoNormalizado: item.tipoNormalizado,
    description: item.description,
    storeMm: {
      width: item.width,
      height: item.height,
      thickness: item.thickness,
    },
    glbBaseMm: glb,
    freeDimensionCatalogMm: item.freeDimensionCatalogMm,
    freeDimensionScaleBaseMm: item.freeDimensionScaleBaseMm,
    freeDimensionFlags: {
      anchoViaGlbMorph: item.freeDimensionAnchoViaGlbMorph,
      altoViaGlbMorph: item.freeDimensionAltoViaGlbMorph,
      fondoViaGlbMorph: item.freeDimensionFondoViaGlbMorph,
    },
    morphsAvailable: compactMorphs(item.morphs),
    morphsActive: compactMorphs(item.morphs)?.map((m) => ({
      name: m.name,
      default: m.default,
      _default: m._default,
    })),
    hasMorphs: item.hasMorphs,
    willNeedIndependentZocalo: zocalo.willNeed,
    zocaloTypeHint: zocalo.zocaloTypeHint,
    matrixElements: item.matrix?.elements
      ? Array.from(item.matrix.elements).map((n) => Number(Number(n).toFixed(4)))
      : undefined,
    boundingBoxHalfSize: item.boundingBox?.halfSize
      ? {
          x: item.boundingBox.halfSize.x,
          y: item.boundingBox.halfSize.y,
          z: item.boundingBox.halfSize.z,
        }
      : undefined,
    extra,
  }
}

/**
 * Log estructurado del flujo de dimensiones.
 * Uso: logDimFlow({ file, fn, phase, reason, snapshot })
 */
export function logDimFlow(payload: DimFlowLogPayload): void {
  if (!isDimFlowDiagnosticEnabled()) return
  showBannerOnce()
  seq += 1
  const { file, fn, phase, reason, snapshot } = payload
  const label = `[DimFlow #${seq}] ${phase} · ${fn}`
  const detail = {
    orden: seq,
    archivo: file,
    funcion: fn,
    fase: phase,
    motivo: reason,
    momento: new Date().toISOString(),
    id: snapshot.id,
    catalogID: snapshot.catalogID,
    referencia: snapshot.referencia,
    tipoNormalizado: snapshot.tipoNormalizado,
    description: snapshot.description,
    storeMm: snapshot.storeMm,
    glbBaseMm: snapshot.glbBaseMm,
    freeDimensionCatalogMm: snapshot.freeDimensionCatalogMm,
    freeDimensionScaleBaseMm: snapshot.freeDimensionScaleBaseMm,
    freeDimensionFlags: snapshot.freeDimensionFlags,
    morphsAvailable: snapshot.morphsAvailable,
    morphsActive: snapshot.morphsActive,
    scaleApplied: snapshot.scaleApplied,
    visualEstMm: snapshot.visualEstMm,
    sidebarMm: snapshot.sidebarMm,
    usesMorph: snapshot.usesMorph,
    usesScale: snapshot.usesScale,
    usesFreeDimension: snapshot.usesFreeDimension,
    hasMorphs: snapshot.hasMorphs,
    willNeedIndependentZocalo: snapshot.willNeedIndependentZocalo,
    zocaloTypeHint: snapshot.zocaloTypeHint,
    boundingBoxHalfSize: snapshot.boundingBoxHalfSize,
    matrixElements: snapshot.matrixElements,
    extra: snapshot.extra,
  }
  // groupCollapsed: visible en consola, expandible sin saturar
  console.groupCollapsed(
    `%c${label}`,
    'color:#0a7;font-weight:bold',
    reason,
  )
  console.log(detail)
  console.groupEnd()
}

/** Reset contador (útil en tests o al recargar escena). */
export function resetDimFlowSeq(): void {
  seq = 0
}

export function isDimFlowEnabled(): boolean {
  return isDimFlowDiagnosticEnabled()
}
